/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component,
	Input,
	OnInit
} from '@angular/core';
import {
	Router
} from '@angular/router';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	EntityTypeApiService
} from '@api/services/entities/entity-type.api.service';
import {
	ClaimConstants
} from '@claims/constants/claims-constants';
import {
	DynamicWizardComponent
} from '@dynamicComponents/dynamic-wizard/dynamic-wizard.component';
import {
	EntityService
} from '@entity/services/entity.service';
import {
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	ObjectHelper
} from '@shared/helpers/object.helper';
import {
	Activity
} from '@shared/implementations/application-data/activity';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IWizardContext
} from '@shared/interfaces/dynamic-interfaces/wizard-context.interface';
import {
	ActivityService
} from '@shared/services/activity.service';
import {
	ModuleService
} from '@shared/services/module.service';
import {
	SessionService
} from '@shared/services/session.service';

/* eslint-enable max-len */

@Component({
	selector: 'reject-claim-payment-claim',
	templateUrl: './reject-claim-payment-claim.component.html'
})

/**
 * A component representing a wizard step for rejecting
 * a claim payment.
 *
 * @export
 * @class RejectClaimPaymentClaimComponent
 * @implements {OnInit}
 * @implements {IDynamicComponent<DynamicWizardComponent, IWizardContext>}
 */
export class RejectClaimPaymentClaimComponent
implements OnInit, IDynamicComponent<DynamicWizardComponent, IWizardContext>
{
	/**
	 * Initializes an instance of the reject claim payment claim component.
	 *
	 * @param {Router} router
	 * The router used for navigation and url query parameter storage.
	 * @param {ActivityService} activityService
	 * The activity message service used to notify the user.
	 * @param {ModuleService} moduleService
	 * The module service used to set module changes on entity creation.
	 * @param {EntityService} entityService
	 * The entity service used to lookup entity modules upon creation.
	 * @param {EntityTypeApiService} entityTypeApiService
	 * The entity type api service used in this component.
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance api service used in this component.
	 * @param {SessionService} sessionService
	 * The session service used in this component.
	 * @memberof RejectClaimPaymentClaimComponent
	 */
	public constructor(
		public router: Router,
		public activityService: ActivityService,
		public moduleService: ModuleService,
		public entityService: EntityService,
		public entityTypeApiService: EntityTypeApiService,
		public entityInstanceApiService: EntityInstanceApiService,
		public sessionService: SessionService)
	{
	}

	/**
	 * Gets or sets the context of this dynamic component that will be set
	 * during initialization. The source is the content component and
	 * the data will be associated data that we desire to pass explicitly.
	 *
	 * @type {IDynamicComponentContext<
	 * 	DynamicWizardComponent,
	 * 	IWizardContext>}
	 * @memberof RejectClaimPaymentClaimComponent
	 */
	@Input() public context: IDynamicComponentContext<
		DynamicWizardComponent,
		IWizardContext>;

	/**
	 * Gets or sets the formly layout used in implementing components.
	 *
	 * @type {FormlyFieldConfig[]}
	 * @memberof RejectClaimPaymentClaimComponent
	 */
	public dynamicFormlyLayout: FormlyFieldConfig[];

	/**
	 * Gets or sets the claim payment id.
	 *
	 * @type {number}
	 * @memberof RejectClaimPaymentClaimComponent
	 */
	private claimPaymentId: number;

	/**
	 * Sets the note string.
	 *
	 * @type {string}
	 * @memberof RejectClaimPaymentClaimComponent
	 */
	private note: string;

	/**
	 * Implements the on initialization interface.
	 *
	 * @async
	 * @memberof RejectClaimPaymentClaimComponent
	 */
	public async ngOnInit(): Promise<void>
	{
		this.claimPaymentId =
			this.context.source.activeMenuItem
				.currentData.data.id;

		this.context.source.addToNext(this.confirmRejection.bind(this));

		await this.performPostInitActions();

		this.context.source.wizardStepLoading = false;
	}

	/**
	 * This will send the reject claim payment event and navigate to
	 * the claim payment.
	 *
	 * @async
	 * @memberof RejectClaimPaymentClaimComponent
	 */
	public async confirmRejection(): Promise<void>
	{
		await this.rejectClaimPayment();
		await this.navigateToClaimPayment(
			this.claimPaymentId,
			ClaimConstants.claimEntityTypeGroups.claimPayments
		);
	}

	/**
	 * Executes the reject claim payment process by executing the
	 * RejectClaimPayment workflow action to update the claim payment status.
	 *
	 * @async
	 * @memberof RejectClaimPaymentClaimComponent
	 */
	public async rejectClaimPayment(): Promise<void>
	{
		this.note =
			this.context.source.activeMenuItem
				.currentData.data.note.content;

		const queryString: string =
			this.entityInstanceApiService.formUrlParam(
				AppConstants.empty,
				{
					note: this.note
				});

		setTimeout(
			() =>
			{
				this.context.source.wizardStepLoading = true;
			});

		return this.activityService.handleActivity(
			new Activity(
				new Promise(
					async(resolve: any) =>
					{
						this.entityInstanceApiService
							.entityInstanceTypeGroup =
								ClaimConstants.claimEntityTypeGroups.claimPayments;
						const baseUrl: string =
							this.entityInstanceApiService.getBaseUrl();

						await this.entityInstanceApiService.executeAction(
							this.claimPaymentId,
							ClaimConstants.workflowActions.rejectClaimPayment,
							null,
							queryString);

						await this.entityInstanceApiService
							.resetAssociatedCache(
								`${baseUrl}/${this.claimPaymentId}`);

						resolve();
					}),
				'<strong>Rejecting Claim Payment</strong>',
				'<strong>Claim Payment Rejected</strong>',
				'Claim Payment has been rejected.',
				'Claim Payment has not been rejected.'));
	}

	/**
	 * Handles the validity changed event sent from the child dynamic
	 * formly component. This will update the validity of the form for
	 * action buttons.
	 *
	 * @async
	 * @param {boolean} isValid
	 * The validity of the current displayed step data set.
	 * @memberof RejectClaimPaymentClaimComponent
	 */
	public async validityChanged(
		isValid: boolean): Promise<void>
	{
		this.context.source.validStepChanged(isValid);
	}

	/**
	 * This will navigate to the claim payment layout using the
	 * claim payment id provided.
	 *
	 * @async
	 * @param {number} entityId
	 * The claim payment entity id to navigate.
	 * @param {string} group
	 * The entity group associated to the navigation.
	 * @memberof RejectClaimPaymentClaimComponent
	 */
	private async navigateToClaimPayment(
		entityId: number,
		group: string)
	{
		this.context.source.addOrUpdateStepData(
			<object>
			{
				automateVerify: false
			});

		this.router.navigate(
			[
				`${this.moduleService.name}/entities`,
				group,
				AppConstants.viewTypes.edit,
				entityId
			],
			{
				queryParams: {
					routeData:
						ObjectHelper.mapRouteData(
							{
								layoutType:
									AppConstants.layoutTypes.full
							})
				}
			});
	}

	/**
	 * Handles the post initialization action.
	 * This will create the dynamic formly layout for display component creation
	 * and permissions.
	 *
	 * @memberof RejectClaimPaymentClaimComponent
	 */
	private async performPostInitActions(): Promise<void>
	{
		this.dynamicFormlyLayout =
			<FormlyFieldConfig[]>
			[
				<FormlyFieldConfig>
				{
					key: 'data.note.content',
					type: FormlyConstants.customControls.customTextArea,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Reason for Rejection',
						description: 'Rejection reason.',
						placeholder: 'Found to be a fraudulent claim.',
						rows: 1,
						gridColumns: 12,
						required: true
					}
				}
			];
	}
}